import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { AuthContext } from '../context/AuthContext';

export default function PagosView({ ultimoPagoId, filtroPedidoId = null, pagoDirecto = null }) {
  const { user } = useContext(AuthContext); 
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const ultimaCargaRef = useRef("");
  const [cargandoImagen, setCargandoImagen] = useState(false);

  const obtenerIconoMetodo = (metodo) => {
    const m = (metodo || "").toLowerCase();
    if (m.includes("debito") || m.includes("débito")) return "bi-credit-card";
    if (m.includes("efectivo")) return "bi-cash-stack";
    if (m.includes("movil") || m.includes("móvil")) return "bi-phone";
    if (m.includes("transferencia")) return "bi-bank";
    return "bi-wallet2";
  };

  // 🎯 FUNCIÓN CENTRAL DE CARGA: Blindada con Token y URL Inteligente
  const cargarPagosReales = useCallback(async () => {
    const token = user?.token || localStorage.getItem('token');
    
    if (!token) {
      setCargando(false);
      return;
    }

    try {
      setCargando(true);
      setCargandoImagen(true);
      
      const idABuscar = filtroPedidoId || ultimoPagoId;

      // --- 🌍 DETECTAR URL DE CONEXIÓN INTELIGENTE ---
      let URL_BASE = 'https://clavelinacarnes.com/clavelina-backend-php/public/api';
      try {
          if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
              URL_BASE = import.meta.env.VITE_API_URL;
          } else if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) {
              URL_BASE = process.env.REACT_APP_API_URL;
          }
      } catch (e) {
          URL_BASE = 'https://clavelinacarnes.com/clavelina-backend-php/public/api';
      }

      // 🔗 Construimos la URL limpia unificada para la API híbrida
      const baseUrl = `${URL_BASE}/admin/consultar_pago`;
      const url = idABuscar && idABuscar !== "general"
          ? `${baseUrl}?pedido_id=${idABuscar}`
          : baseUrl;

      console.log(`📡 Solicitando comprobante a la API: ${url}`);

      const res = await fetch(url, {
          method: 'GET',
          headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
          }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const resultado = await res.json();
      const datosReales = resultado.data || [];
      
      setPagos(Array.isArray(datosReales) ? datosReales : []);
      
    } catch (err) {
      console.error("❌ Error cargando pagos desde la API unificada:", err.message);
    } finally {
      setCargando(false);
      setCargandoImagen(false);
    }
  }, [filtroPedidoId, ultimoPagoId, user?.token]);

  // 🔄 SINCRONIZACIÓN INMEDIATA CUANDO LLEGA PAGO DIRECTO (PUSHER)
  useEffect(() => {
    if (pagoDirecto) {
      const pagoFormateado = {
        pago_id: pagoDirecto.id || pagoDirecto.pago_id || `temp-${Date.now()}`,
        pedido_id: pagoDirecto.pedido_id,
        metodo: pagoDirecto.metodo || 'pago móvil',
        datos: pagoDirecto.datos || {},
        imagen: pagoDirecto.imagen || pagoDirecto.imagen_pago || pagoDirecto.imagen_nombre,
        requiereCarga: true,
        tiene_imagen: true,
      };
      
      setPagos([pagoFormateado]);
      setCargando(false);
      
      // Si el evento de Pusher no trajo la imagen adjunta, vamos a interrogar a la base de datos de cPanel
      if (!pagoFormateado.imagen) {
        cargarPagosReales();
      }
    }
  }, [pagoDirecto, cargarPagosReales]);

  // Carga inicial y control de cambios por ID de pedido
  useEffect(() => {
    if (pagoDirecto) return;
    
    const idPedido = filtroPedidoId || ultimoPagoId || "general";
    if (ultimaCargaRef.current !== idPedido) {
      ultimaCargaRef.current = idPedido;
      cargarPagosReales();
    }
  }, [filtroPedidoId, ultimoPagoId, cargarPagosReales, pagoDirecto]);

  return (
    <div className="w-100">
      <div className="row g-0">
        {cargando ? (
          <div className="col-12 text-center py-3 text-secondary small">
            <div className="spinner-border spinner-border-sm text-danger me-2"></div>
            Sincronizando comprobante...
          </div>
        ) : pagos.length > 0 ? (
          pagos.map((p, index) => {
            // 📥 Extraemos la imagen de forma unificada respetando el formato del nuevo PHP
            const datoImagen = p.imagen || p.imagen_pago || p.imagen_comprobante; 
            
            let fuenteImagen = null;
            if (datoImagen) {
              fuenteImagen = datoImagen.startsWith('data:') 
                ? datoImagen 
                : `data:image/jpeg;base64,${datoImagen.trim()}`;
            }

            const metodo = (p.metodo || "").toLowerCase();
            const debeMostrar = metodo.includes("movil") || metodo.includes("móvil") || !!fuenteImagen;

            console.log(`📸 Renderizando Pago ${p.pago_id || index}:`, fuenteImagen ? "Imagen detectada" : "Sin imagen");

            return (
              <div key={p.pago_id || index} className="col-12 mb-3">
                <div className="card bg-black border-secondary shadow-sm">
                  <div className="card-body p-2">
                    
                    {/* Encabezado con método y precio */}
                    <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                      <span className="text-white small fw-bold">
                        <i className={`bi ${obtenerIconoMetodo(p.metodo)} me-2 text-danger`}></i>
                        {p.metodo?.toUpperCase() || 'PAGO'}
                      </span>
                      {p.precio && <span className="badge bg-danger">${p.precio}</span>}
                    </div>

                    {/* Visualizador de Comprobante */}
                    {debeMostrar && (
                      <div className="position-relative bg-dark rounded overflow-hidden" 
                           style={{ minHeight: "200px", maxHeight: "450px", border: "1px solid #333" }}>
                        
                        {fuenteImagen ? (
                          <img 
                            src={fuenteImagen} 
                            alt="Comprobante de Pago"
                            className="img-fluid w-100 h-100" 
                            style={{ 
                              objectFit: 'contain',
                              display: 'block',
                              cursor: 'pointer',
                              backgroundColor: '#1a1a1a' 
                            }} 
                            onClick={() => window.open(fuenteImagen, '_blank')}
                            onError={(e) => {
                              console.error("❌ Error al renderizar Base64");
                              e.target.src = "https://via.placeholder.com/350?text=Error+en+comprobante";
                            }}
                          />
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                            <i className="bi bi-hourglass-split fs-2 mb-2 animate__animated animate__rotateIn animate__infinite text-danger"></i>
                            <p className="small mb-0">Esperando imagen desde el servidor...</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-12 text-center text-muted py-3 small">
            <p className="mb-0">No se encontraron pagos registrados para este pedido.</p>
          </div>
        )}
      </div>
    </div>
  );
}